import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { DotsIndicator } from '@garnish/core';

import {
  BagRewardsEmptyState,
  BagRewardsRail,
  LoyaltyReward,
  LoyaltyRewardSkeleton,
  useBagRewardsRail,
} from '@sg/shared-fe-features-loyalty';

import { Loyalty } from '@order/features-loyalty';
import { Bag } from '@order/features-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useBagContent, useLoyaltyContent, useLoyaltyPointsRewards } from '../../../../hooks';
import { useBagLoyalty, type useOrderState } from '../../hooks';

/**
 * The loyalty section on the bag.
 */
export const BagLoyalty = (props: BagLoyaltyProps) => {
  const { orderState } = props;

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    isFetchingRewards,
    hasFailedToLoadRewards,
    isApplyingPromoCode,
    isPerformingBagAction,
    rewardIdBeingUpdated,
    rewards,
    appliedRewardId,
    availablePoints,
    customer: { isNewCustomer, isOptedOutFromLoyalty },
    bagTracking: { trackLoyaltySignedOutPressed },
  } = orderState;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    showLoyaltyInfo,
    showLoyaltyTerms,
    applyBagReward,
    removeBagReward,
    refetchRewards,
    applyBagPromoCode,
  } = orderState;

  // ─── Auth State ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();

  const handleCreateAccount = useCallback(() => {
    trackLoyaltySignedOutPressed();

    router.dismissTo({
      pathname: '/(auth)/join-or-sign-in',
      params: { redirect: '/bag' },
    });
  }, [router, trackLoyaltySignedOutPressed]);

  // ─── Promo Code New Customer Filtering ────────────────────────────────────

  const isPromoCodeNewCustomerFilteringEnabled = useFeatureFlag(
    'CELS-2917-reject-promo-code-previous-orders-enabled',
  );

  const shouldShowPromoCodeField =
    isLoggedIn && (!isPromoCodeNewCustomerFilteringEnabled || isNewCustomer);

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    bagHeaderLoggedOut: loyaltyTitleLoggedOut,
    bagHeaderNewUser: loyaltyTitleNewUser,
    bagHeaderRecurringUser: loyaltyTitleRecurringUser,
    bagHeaderSubtitle: loyaltySubtitle,
    bagHeaderLoggedOutImageMessage: loyaltyImageText,
    bagHeaderLoggedOutImage: loyaltyImage,
  } = useLoyaltyContent({ availablePoints });

  const { rewardExpirationDate: expirationDateLabel } = useBagContent();

  const { title: loyaltyHeaderTitle } = useBagLoyalty({
    isLoggedIn,
    isNewCustomer: availablePoints === 0,
    loyaltyTitleLoggedOut,
    loyaltyTitleNewUser,
    loyaltyTitleRecurringUser,
    availablePoints,
  });

  // ─── Dynamic Subtitle ─────────────────────────────────────────────────────

  const { pointsRewards } = useLoyaltyPointsRewards();

  // ─── Flags ────────────────────────────────────────────────────────────────

  const hasLoyaltyV2Experience = !isOptedOutFromLoyalty;

  const hasRewards = rewards.length > 0;
  const shouldShowLoadingSkeleton = !hasRewards && isFetchingRewards;
  const shouldShowEmptyState =
    isLoggedIn &&
    !hasRewards &&
    !isFetchingRewards &&
    !hasFailedToLoadRewards &&
    !isOptedOutFromLoyalty;
  const shouldShowInfoIcon = isLoggedIn;

  // ─── Scroll Indicator ─────────────────────────────────────────────────────

  const { setCurrentRailWidth, currentlyScrolledReward, handleOnScroll } = useBagRewardsRail({
    rewardsCount: rewards.length,
  });

  if (isOptedOutFromLoyalty && !shouldShowPromoCodeField) return null;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.PromoRewardsContainer>
      {hasLoyaltyV2Experience ? (
        <Loyalty.Bag.LoyaltyHeader
          title={loyaltyHeaderTitle}
          onPressInfo={shouldShowInfoIcon ? showLoyaltyInfo : undefined}
        />
      ) : null}

      {hasLoyaltyV2Experience && !isLoggedIn ? (
        <Loyalty.Bag.LoyaltySignedOutCta
          message={loyaltyImageText}
          imageUrl={loyaltyImage}
          onPress={handleCreateAccount}
        />
      ) : null}

      {hasLoyaltyV2Experience && hasFailedToLoadRewards ? (
        <Loyalty.Bag.LoyaltyErrorState refetch={refetchRewards} />
      ) : null}

      {shouldShowLoadingSkeleton ? (
        <BagRewardsRail heading={loyaltySubtitle} showNavControls={false}>
          <LoyaltyRewardSkeleton />
          <LoyaltyRewardSkeleton />
        </BagRewardsRail>
      ) : null}

      {hasLoyaltyV2Experience && hasRewards ? (
        <BagRewardsRail
          heading={loyaltySubtitle}
          onScroll={handleOnScroll}
          onContentSizeChange={setCurrentRailWidth}
        >
          {rewards.map((reward) => {
            const isApplied = appliedRewardId === reward.id;
            const isLoading = rewardIdBeingUpdated === reward.id;
            const isDisabled = isPerformingBagAction && !isLoading;
            const onPress = isApplied ? removeBagReward : applyBagReward;

            const onShowTerms = reward.terms
              ? () => {
                  showLoyaltyTerms(reward.id);
                }
              : undefined;

            return (
              <LoyaltyReward
                key={reward.id}
                id={reward.id}
                title={reward.name}
                points={reward.points}
                imageUrl={reward.assetUrl}
                expirationDate={reward.expirationDate}
                expirationDateLabel={expirationDateLabel}
                redeemableAt={reward.redeemableAt}
                isApplied={isApplied}
                isLoading={isLoading}
                isDisabled={isDisabled}
                onPress={onPress}
                onShowTerms={onShowTerms}
              />
            );
          })}
        </BagRewardsRail>
      ) : null}

      {hasLoyaltyV2Experience && rewards.length > 1 ? (
        <DotsIndicator length={rewards.length} active={currentlyScrolledReward} />
      ) : null}

      {shouldShowEmptyState ? (
        <BagRewardsEmptyState pointsRewards={pointsRewards} availablePoints={availablePoints} />
      ) : null}

      {shouldShowPromoCodeField ? (
        <Bag.PromoField
          rewards={rewards}
          isLoading={isApplyingPromoCode}
          onPress={applyBagPromoCode}
          isDisabled={Boolean(isPerformingBagAction && !isApplyingPromoCode)}
        />
      ) : null}
    </Bag.PromoRewardsContainer>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagLoyaltyProps = {
  orderState: ReturnType<typeof useOrderState>;
};
